///
/// This file defines all the steps that modules go through throughout their
/// production process. PRODUCTION_STEPS_2S and PRODUCTION_STEPS_PS define
/// all the possible steps for 2S and PS modules respectively, and the
/// AdjacentStepsMap defines the relationship between each step and its
/// neighbors in the graph.
///
/// To ensure production steps are completed in the correct order, the code
/// needs to be able to tell all of the following:
///  - If a step being performed is a valid step that can be performed.
///  - If a step being performed has it's necessary pre-requisties completed.
///  - The next step that needs to be performed given a "last step completed".
///

/**
 * The universe of possible production steps for 2S modules.
 */
export const PRODUCTION_STEPS_2S = {
  STEP_2S_SENSOR_VISUAL_INSPECTION: "2S_SENSOR_VISUAL_INSPECTION",
  STEP_2S_SENSOR_KAPTON_GLUING: "2S_SENSOR_KAPTON_GLUING",
  STEP_2S_SENSOR_HV_TAIL_GLUING: "2S_SENSOR_HV_TAIL_GLUING",
  STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL:
    "2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL",
  STEP_2S_SENSOR_IV_MEASURMENT: "2S_SENSOR_IV_MEASURMENT",

  STEP_2S_GLUE_SANDWICH: "2S_GLUE_SANDWICH",
  STEP_2S_SANDWICH_VISUAL_INSPECTION: "2S_SANDWICH_VISUAL_INSPECTION",
  STEP_2S_SANDWICH_METROLOGY_TEST: "2S_SANDWICH_METROLOGY_TEST",

  STEP_2S_SEH_VISUAL_INSPECTION: "2S_SEH_VISUAL_INSPECTION",
  STEP_2S_FEH_L_VISUAL_INSPECTION: "2S_FEH_VISUAL_INSPECTION",
  STEP_2S_FEH_R_VISUAL_INSPECTION: "2S_FEH_VISUAL_INSPECTION",
  STEP_2S_SKELETON_ASSEMBLY: "2S_SKELETON_ASSEMBLY",
  STEP_2S_SKELETON_TEST: "2S_SKELETON_TEST",

  STEP_2S_VTRX_VISUAL_INSPECTION: "2S_VTRX_VISUAL_INSPECTION",

  STEP_2S_GLUE_HYBRID: "2S_GLUE_HYBRID",
  STEP_2S_MODULE_VISUAL_INSPECTION: "2S_MODULE_VISUAL_INSPECTION",
  STEP_2S_PACK_AND_SHIP_MODULE: "2S_PACK_AND_SHIP_MODULE",
  STEP_2S_RETRIEVE_AND_INSPECT_MODULE: "2S_RETRIEVE_AND_INSPECT_MODULE",
  STEP_2S_ASSEMBLE_TOP_WIREBOND: "2S_ASSEMBLE_FIRST_SENSOR_WIREBOND",
  STEP_2S_ASSEMBLE_BOTTOM_WIREBOND: "2S_ASSEMBLE_SECOND_SENSOR_WIREBOND",
  STEP_2S_ASSEMBLE_TOP_WIREBOND_OLD: "2S_ASSEMBLE_TOP_WIREBOND",
  STEP_2S_ASSEMBLE_BOTTOM_WIREBOND_OLD: "2S_ASSEMBLE_BOTTOM_WIREBOND",
  STEP_2S_ATTACH_VTRX: "2S_ATTACH_VTRX",
  STEP_2S_PRE_ENCAPSULATION_TEST: "2S_PRE_ENCAPSULATION_TEST",
  STEP_2S_TOP_ENCAPSULATION: "2S_FIRST_SENSOR_ENCAPSULATION",
  STEP_2S_BOTTOM_ENCAPSULATION: "2S_SECOND_SENSOR_ENCAPSULATION",
  STEP_2S_TOP_ENCAPSULATION_OLD: "2S_TOP_ENCAPSULATION",
  STEP_2S_BOTTOM_ENCAPSULATION_OLD: "2S_BOTTOM_ENCAPSULATION",
  STEP_2S_FINAL_VISUAL_INSPECTION: "2S_FINAL_VISUAL_INSPECTION",
  STEP_2S_BURN_IN_TEST: "2S_BURN_IN_TEST",
  STEP_2S_SHIP_TO_INTEGRATION: "2S_SHIP_TO_INTEGRATION",
} as const;

/**
 * The universe of possible production steps for PS modules.
 */
export const PRODUCTION_STEPS_PS = {
  STEP_PS_SENSOR_VISUAL_INSPECTION: "PS_SENSOR_VISUAL_INSPECTION",
  STEP_PS_GLUE_HV_TAIL_TO_SENSOR: "PS_GLUE_HV_TAIL_TO_SENSOR",
  STEP_PS_ENCAPSULATE_HV_TAIL: "PS_ENCAPSULATE_HV_TAIL",
  STEP_PS_SENSOR_IV_MEASURMENT: "PS_SENSOR_IV_MEASURMENT",

  STEP_PS_MA_PSA_VISUAL_INSPECTION: "PS_MA_PSA_VISUAL_INSPECTION",
  STEP_PS_TEST_MA_PSA: "PS_TEST_MA_PSA",

  STEP_PS_VTRX_VISUAL_INSPECTION: "PS_VTRX_VISUAL_INSPECTION",

  STEP_PS_CF_BASEPLATE_IMPORT: "PS_CF_BASEPLATE_IMPORT",
  STEP_PS_CF_BASEPLATE_INSERT_GLUING: "PS_CF_BASEPLATE_INSERT_GLUING",
  STEP_PS_CF_BASEPLATE_KAPTON_GLUING: "PS_CF_BASEPLATE_KAPTON_GLUING",
  STEP_PS_MA_PSA_BASEPLATE_GLUING: "PS_MA_PSA_BASEPLATE_GLUING",

  STEP_PS_SENSOR_SPACER_GLUING: "PS_SENSOR_SPACER_GLUING",
  STEP_PS_SENSOR_BASEPLATE_GLUING: "PS_SENSOR_BASEPLATE_GLUING",
  STEP_PS_SANDWICH_VISUAL_INSPECTION: "PS_SANDWICH_VISUAL_INSPECTION",
  STEP_PS_TEST_SANDWICH_METROLOGY: "PS_TEST_SANDWICH_METROLOGY",

  STEP_PS_FEH_VISUAL_INSPECTION: "PS_FEH_VISUAL_INSPECTION",
  STEP_PS_POH_VISUAL_INSPECTION: "PS_POH_VISUAL_INSPECTION",
  STEP_PS_ROH_VISUAL_INSPECTION: "PS_ROH_VISUAL_INSPECTION",
  STEP_PS_SANDWICH_HYBRID_GLUING: "PS_SANDWICH_HYBRID_GLUING",
  STEP_PS_MODULE_PRE_SHIP_VISUAL_INSPECTION:
    "PS_MODULE_PRE_SHIP_VISUAL_INSPECTION",
  STEP_PS_PACK_AND_SHIP_MODULE: "PS_PACK_AND_SHIP_MODULE",

  STEP_PS_RETRIEVE_AND_INSPECT_MODULE: "PS_RETRIEVE_AND_INSPECT_MODULE",
  STEP_PS_TOP_SENSOR_WIREBOND: "PS_FIRST_SENSOR_WIREBOND",
  STEP_PS_BOTTOM_SENSOR_WIREBOND: "PS_SECOND_SENSOR_WIREBOND",
  STEP_PS_TOP_SENSOR_WIREBOND_OLD: "PS_TOP_SENSOR_WIREBOND",
  STEP_PS_BOTTOM_SENSOR_WIREBOND_OLD: "PS_BOTTOM_SENSOR_WIREBOND",
  STEP_PS_ATTACH_VTRX: "PS_ATTACH_VTRX",
  STEP_PS_PRE_ENCAPSULATION_TEST: "PS_PRE_ENCAPSULATION_TEST",
  STEP_PS_TOP_SENSOR_ENCAPSULATE: "PS_FIRST_SENSOR_ENCAPSULATE",
  STEP_PS_BOTTOM_SENSOR_ENCAPSULATE: "PS_SECOND_SENSOR_ENCAPSULATE",
  STEP_PS_TOP_SENSOR_ENCAPSULATE_OLD: "PS_TOP_SENSOR_ENCAPSULATE",
  STEP_PS_BOTTOM_SENSOR_ENCAPSULATE_OLD: "PS_BOTTOM_SENSOR_ENCAPSULATE",
  STEP_PS_POST_ENCAPSULATION_VISUAL_INSPECTION:
    "PS_POST_ENCAPSULATION_VISUAL_INSPECTION",
  STEP_PS_BURN_IN: "PS_BURN_IN",
  STEP_PS_POST_BURN_IN_VISUAL_INSPECTION: "PS_POST_BURN_IN_VISUAL_INSPECTION",
  STEP_PS_FINAL_PACK_AND_SHIP: "PS_FINAL_PACK_AND_SHIP",
} as const;

/**
 * The universe of possible production steps that DO NOT progress a module through it's production cycle.
 */
export const PRODUCTION_STEPS_AUX = {
  STEP_AUX_SCRATCHPAD: "AUX_SCRATCHPAD",
  STEP_AUX_COMMENT: "AUX_COMMENT",
  STEP_AUX_IMAGE: "AUX_IMAGE",
  STEP_AUX_TAG: "AUX_TAG",
  STEP_AUX_DEBUG_TAG: "AUX_DEBUG_TAG",
  STEP_AUX_REMOVE_DEBUG_TAG: "AUX_REMOVE_DEBUG_TAG",
  STEP_AUX_IMPORT_MODULE: "AUX_IMPORT_MODULE",
  STEP_AUX_SHIP_MODULE: "AUX_SHIP_MODULE",
  STEP_AUX_DETACH_CARRIER: "AUX_DETACH_CARRIER",
  STEP_AUX_DETACH_VTRX: "AUX_DETACH_VTRX",
  STEP_AUX_DETACH_SEH: "AUX_DETACH_SEH",
  STEP_AUX_DETACH_ROH: "AUX_DETACH_ROH",
  STEP_AUX_2S_EXTRA_GLUING: "AUX_2S_EXTRA_GLUING",
  STEP_AUX_PS_EXTRA_GLUING: "AUX_PS_EXTRA_GLUING",
} as const;

export type Steps2S =
  (typeof PRODUCTION_STEPS_2S)[keyof typeof PRODUCTION_STEPS_2S];
export type StepsPS =
  (typeof PRODUCTION_STEPS_PS)[keyof typeof PRODUCTION_STEPS_PS];
export type StepsAux =
  (typeof PRODUCTION_STEPS_AUX)[keyof typeof PRODUCTION_STEPS_AUX];
export type StepsAny = Steps2S | StepsPS | StepsAux;

export function isStep2S(stepName: string): stepName is Steps2S {
  return (Object.values(PRODUCTION_STEPS_2S) as string[]).includes(stepName);
}

export function isStepPS(stepName: string): stepName is StepsPS {
  return (Object.values(PRODUCTION_STEPS_PS) as string[]).includes(stepName);
}

export function isStepsAux(stepName: string): stepName is StepsAux {
  return (Object.values(PRODUCTION_STEPS_AUX) as string[]).includes(stepName);
}

export function isStepsAny(stepName: string): stepName is StepsAny {
  return isStep2S(stepName) || isStepPS(stepName) || isStepsAux(stepName);
}

export type AdjacentSteps = {
  previous: StepsAny[];
  next: StepsAny[];
};

/**
 * A map that defines the dependency graph for production steps as modules go
 * through production.
 */
export const AdjacentStepsMap: Map<StepsAny, AdjacentSteps> = new Map([
  // 2S STEPS
  [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_KAPTON_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_KAPTON_GLUING,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_HV_TAIL_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_HV_TAIL_GLUING,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_KAPTON_GLUING],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_HV_TAIL_GLUING],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_IV_MEASURMENT],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SENSOR_IV_MEASURMENT,
    {
      previous: [
        PRODUCTION_STEPS_2S.STEP_2S_SENSOR_BOND_AND_ENCAPSULATE_HV_TAIL,
      ],
      next: [PRODUCTION_STEPS_2S.STEP_2S_GLUE_SANDWICH],
    },
  ],

  [
    PRODUCTION_STEPS_2S.STEP_2S_GLUE_SANDWICH,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SENSOR_IV_MEASURMENT],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_GLUE_SANDWICH],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_METROLOGY_TEST],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_METROLOGY_TEST,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_GLUE_HYBRID],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_VTRX_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ATTACH_VTRX],
    },
  ],

  [
    PRODUCTION_STEPS_2S.STEP_2S_SEH_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SKELETON_ASSEMBLY],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_FEH_L_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SKELETON_ASSEMBLY],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_FEH_R_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SKELETON_ASSEMBLY],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SKELETON_ASSEMBLY,
    {
      previous: [
        PRODUCTION_STEPS_2S.STEP_2S_SEH_VISUAL_INSPECTION,
        PRODUCTION_STEPS_2S.STEP_2S_FEH_L_VISUAL_INSPECTION,
        PRODUCTION_STEPS_2S.STEP_2S_FEH_R_VISUAL_INSPECTION,
      ],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SKELETON_TEST],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SKELETON_TEST,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_SKELETON_ASSEMBLY],
      next: [PRODUCTION_STEPS_2S.STEP_2S_GLUE_HYBRID],
    },
  ],

  [
    PRODUCTION_STEPS_2S.STEP_2S_GLUE_HYBRID,
    {
      previous: [
        PRODUCTION_STEPS_2S.STEP_2S_SANDWICH_METROLOGY_TEST,
        PRODUCTION_STEPS_2S.STEP_2S_SKELETON_TEST,
      ],
      next: [PRODUCTION_STEPS_2S.STEP_2S_MODULE_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_MODULE_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_GLUE_HYBRID],
      next: [PRODUCTION_STEPS_2S.STEP_2S_PACK_AND_SHIP_MODULE],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_PACK_AND_SHIP_MODULE,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_MODULE_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_RETRIEVE_AND_INSPECT_MODULE],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_RETRIEVE_AND_INSPECT_MODULE,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_PACK_AND_SHIP_MODULE],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_TOP_WIREBOND],
    },
  ],

  [
    PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_TOP_WIREBOND_OLD,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_RETRIEVE_AND_INSPECT_MODULE],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_BOTTOM_WIREBOND],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_BOTTOM_WIREBOND_OLD,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_TOP_WIREBOND_OLD],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ATTACH_VTRX],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_TOP_WIREBOND,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_RETRIEVE_AND_INSPECT_MODULE],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_BOTTOM_WIREBOND],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_BOTTOM_WIREBOND,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_TOP_WIREBOND],
      next: [PRODUCTION_STEPS_2S.STEP_2S_ATTACH_VTRX],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_ATTACH_VTRX,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_ASSEMBLE_BOTTOM_WIREBOND],
      next: [PRODUCTION_STEPS_2S.STEP_2S_PRE_ENCAPSULATION_TEST],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_PRE_ENCAPSULATION_TEST,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_ATTACH_VTRX],
      next: [PRODUCTION_STEPS_2S.STEP_2S_TOP_ENCAPSULATION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_TOP_ENCAPSULATION_OLD,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_PRE_ENCAPSULATION_TEST],
      next: [PRODUCTION_STEPS_2S.STEP_2S_BOTTOM_ENCAPSULATION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_BOTTOM_ENCAPSULATION_OLD,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_TOP_ENCAPSULATION_OLD],
      next: [PRODUCTION_STEPS_2S.STEP_2S_FINAL_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_TOP_ENCAPSULATION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_PRE_ENCAPSULATION_TEST],
      next: [PRODUCTION_STEPS_2S.STEP_2S_BOTTOM_ENCAPSULATION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_BOTTOM_ENCAPSULATION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_TOP_ENCAPSULATION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_FINAL_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_FINAL_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_BOTTOM_ENCAPSULATION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_BURN_IN_TEST],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_BURN_IN_TEST,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_FINAL_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_2S.STEP_2S_SHIP_TO_INTEGRATION],
    },
  ],
  [
    PRODUCTION_STEPS_2S.STEP_2S_SHIP_TO_INTEGRATION,
    {
      previous: [PRODUCTION_STEPS_2S.STEP_2S_BURN_IN_TEST],
      next: [],
    },
  ],

  // PS STEPS

  [
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_GLUE_HV_TAIL_TO_SENSOR],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_GLUE_HV_TAIL_TO_SENSOR,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_PS.STEP_PS_ENCAPSULATE_HV_TAIL],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_ENCAPSULATE_HV_TAIL,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_GLUE_HV_TAIL_TO_SENSOR],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_IV_MEASURMENT],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_IV_MEASURMENT,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_ENCAPSULATE_HV_TAIL],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_SPACER_GLUING],
    },
  ],

  [
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_TEST_MA_PSA],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TEST_MA_PSA,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_VTRX_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_ATTACH_VTRX],
    },
  ],

  [
    PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_IMPORT,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_INSERT_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_INSERT_GLUING,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_IMPORT],
      next: [PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_KAPTON_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_KAPTON_GLUING,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_INSERT_GLUING],
      next: [PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
    {
      previous: [
        PRODUCTION_STEPS_PS.STEP_PS_TEST_MA_PSA,
        PRODUCTION_STEPS_PS.STEP_PS_CF_BASEPLATE_KAPTON_GLUING,
      ],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_BASEPLATE_GLUING],
    },
  ],

  [
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_SPACER_GLUING,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_IV_MEASURMENT],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_BASEPLATE_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_SENSOR_BASEPLATE_GLUING,
    {
      previous: [
        PRODUCTION_STEPS_PS.STEP_PS_SENSOR_SPACER_GLUING,
        PRODUCTION_STEPS_PS.STEP_PS_MA_PSA_BASEPLATE_GLUING,
      ],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_SENSOR_BASEPLATE_GLUING],
      next: [PRODUCTION_STEPS_PS.STEP_PS_TEST_SANDWICH_METROLOGY],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TEST_SANDWICH_METROLOGY,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING],
    },
  ],

  [
    PRODUCTION_STEPS_PS.STEP_PS_FEH_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_POH_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_ROH_VISUAL_INSPECTION,
    {
      previous: [],
      next: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING,
    {
      previous: [
        PRODUCTION_STEPS_PS.STEP_PS_TEST_SANDWICH_METROLOGY,
        PRODUCTION_STEPS_PS.STEP_PS_FEH_VISUAL_INSPECTION,
        PRODUCTION_STEPS_PS.STEP_PS_POH_VISUAL_INSPECTION,
        PRODUCTION_STEPS_PS.STEP_PS_ROH_VISUAL_INSPECTION,
      ],
      next: [PRODUCTION_STEPS_PS.STEP_PS_MODULE_PRE_SHIP_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_MODULE_PRE_SHIP_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_SANDWICH_HYBRID_GLUING],
      next: [PRODUCTION_STEPS_PS.STEP_PS_PACK_AND_SHIP_MODULE],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_PACK_AND_SHIP_MODULE,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_MODULE_PRE_SHIP_VISUAL_INSPECTION],
      next: [PRODUCTION_STEPS_PS.STEP_PS_RETRIEVE_AND_INSPECT_MODULE],
    },
  ],

  [
    PRODUCTION_STEPS_PS.STEP_PS_RETRIEVE_AND_INSPECT_MODULE,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_PACK_AND_SHIP_MODULE],
      next: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_WIREBOND],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_WIREBOND_OLD,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_RETRIEVE_AND_INSPECT_MODULE],
      next: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_WIREBOND],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_WIREBOND_OLD,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_WIREBOND_OLD],
      next: [PRODUCTION_STEPS_PS.STEP_PS_ATTACH_VTRX],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_WIREBOND,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_RETRIEVE_AND_INSPECT_MODULE],
      next: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_WIREBOND],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_WIREBOND,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_WIREBOND],
      next: [PRODUCTION_STEPS_PS.STEP_PS_ATTACH_VTRX],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_ATTACH_VTRX,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_WIREBOND],
      next: [PRODUCTION_STEPS_PS.STEP_PS_PRE_ENCAPSULATION_TEST],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_PRE_ENCAPSULATION_TEST,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_ATTACH_VTRX],
      next: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_ENCAPSULATE],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_ENCAPSULATE_OLD,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_PRE_ENCAPSULATION_TEST],
      next: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_ENCAPSULATE],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_ENCAPSULATE_OLD,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_ENCAPSULATE_OLD],
      next: [PRODUCTION_STEPS_PS.STEP_PS_POST_ENCAPSULATION_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_ENCAPSULATE,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_PRE_ENCAPSULATION_TEST],
      next: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_ENCAPSULATE],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_ENCAPSULATE,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_TOP_SENSOR_ENCAPSULATE],
      next: [PRODUCTION_STEPS_PS.STEP_PS_POST_ENCAPSULATION_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_POST_ENCAPSULATION_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_BOTTOM_SENSOR_ENCAPSULATE],
      next: [PRODUCTION_STEPS_PS.STEP_PS_BURN_IN],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_BURN_IN,
    {
      previous: [
        PRODUCTION_STEPS_PS.STEP_PS_POST_ENCAPSULATION_VISUAL_INSPECTION,
      ],
      next: [PRODUCTION_STEPS_PS.STEP_PS_POST_BURN_IN_VISUAL_INSPECTION],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_POST_BURN_IN_VISUAL_INSPECTION,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_BURN_IN],
      next: [PRODUCTION_STEPS_PS.STEP_PS_FINAL_PACK_AND_SHIP],
    },
  ],
  [
    PRODUCTION_STEPS_PS.STEP_PS_FINAL_PACK_AND_SHIP,
    {
      previous: [PRODUCTION_STEPS_PS.STEP_PS_POST_BURN_IN_VISUAL_INSPECTION],
      next: [],
    },
  ],
]);
